import type {
  EmployeeTreatmentStart,
  TreatmentStartDatePrecision,
  TreatmentStartRecord,
  TreatmentStartSource,
  UnderhallIneligibleReason,
} from "@care-by-ella/contracts";

// TES-1073 (Ö-297, Ö-298) — how the documented earlier treatment start and
// the "Ella Underhåll" rule read in admin. Staff-facing; the patient never sees
// this. Pure, so the wording is tested without a DOM.
//
// Emelie: an estimated date is never stored or shown as exact or verified.
// So an estimate always says "uppskattat" and never stands as a bare date,
// and month/year dates never show a day.

export const PRECISION_LABEL: Record<TreatmentStartDatePrecision, string> = {
  exact: "Exakt datum",
  month: "Månad",
  year: "År",
  estimated: "Uppskattat",
};

export const SOURCE_LABEL: Record<TreatmentStartSource, string> = {
  patient_reported: "Patientrapporterat",
  prescription_document: "Receptunderlag",
  medical_record: "Journal",
  doctor_assessed: "Läkarbedömning",
};

export const UNDERHALL_REASON_LABEL: Record<UnderhallIneligibleReason, string> =
  {
    treatment_start_missing: "ingen dokumenterad behandlingsstart",
    estimate_not_confirmed:
      "uppskattat startdatum utan läkarens bekräftelse av tolv hela månader",
    less_than_twelve_months: "mindre än tolv månaders dokumenterad behandling",
    not_requested_by_patient: "patienten har inte själv valt det",
  };

const MONTHS_SV = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december",
];

/** The date as precise as it is known, and never more. */
export function startDateText(
  date: string,
  precision: TreatmentStartDatePrecision,
): string {
  const [y, m] = date.split("-");
  switch (precision) {
    case "exact":
      return date;
    case "month":
      return `${MONTHS_SV[Number(m) - 1] ?? m} ${y}`;
    case "year":
      return y ?? date;
    case "estimated":
      return `ca ${date} (uppskattat)`;
  }
}

const dateSv = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });

/** "mars 2024 · Månad · Journal · registrerat av Dr K 2026-10-02". */
export function recordLine(record: TreatmentStartRecord): string {
  return [
    startDateText(record.treatmentStartedAt, record.datePrecision),
    PRECISION_LABEL[record.datePrecision],
    SOURCE_LABEL[record.source],
    `registrerat av ${record.recordedBy ?? "läkare"} ${dateSv(record.recordedAt)}`,
  ].join(" · ");
}

/** For an estimate: whether the twelve months are confirmed, by whom. */
export function estimateLine(record: TreatmentStartRecord): string | null {
  if (record.datePrecision !== "estimated") return null;
  return record.estimateConfirmedAt
    ? `Tolv hela månader bekräftade av ${record.estimateConfirmedBy ?? "läkare"} ${dateSv(record.estimateConfirmedAt)}.`
    : "Ej bekräftat att uppskattningen visar tolv hela månader – räknas inte.";
}

/**
 * Why "Ella Underhåll" cannot be chosen, or null when it can. The same reading
 * the server's block uses, so the picker never offers what the link refuses.
 */
export function underhallBlockedText(
  underhall: EmployeeTreatmentStart["underhall"] | null,
): string | null {
  if (!underhall || underhall.status === "eligible") return null;
  // No reasons means the read itself failed: unknown is not eligible.
  const reasons =
    underhall.reasons.map((r) => UNDERHALL_REASON_LABEL[r]).join("; ") ||
    "behandlingsstarten kunde inte hämtas";
  const from =
    underhall.eligibleFrom &&
    underhall.reasons.includes("less_than_twelve_months")
      ? ` Tidigast ${underhall.eligibleFrom}.`
      : "";
  return `Ella Underhåll kan inte väljas: ${reasons}.${from}`;
}

/**
 * The ISO date the form sends for what the doctor typed: a month input gives
 * "YYYY-MM", a year input "YYYY". Null while incomplete.
 */
export function startDatePayload(
  raw: string,
  precision: TreatmentStartDatePrecision,
): string | null {
  const v = raw.trim();
  if (precision === "year") return /^\d{4}$/.test(v) ? `${v}-01-01` : null;
  if (precision === "month") return /^\d{4}-\d{2}$/.test(v) ? `${v}-01` : null;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

/** The inverse, to fill the input from a stored or prefilled date. */
export function startDateInput(
  date: string,
  precision: TreatmentStartDatePrecision,
): string {
  if (precision === "year") return date.slice(0, 4);
  if (precision === "month") return date.slice(0, 7);
  return date;
}
